/**
 * bid-tracker.js
 * Q23 · Build-36 — Bid Performance Tracker
 * ──────────────────────────────────────────────────────────
 * Architecture:
 *   - Primary: calls opora-ai worker API (POST /api/bid-tracker/add etc.)
 *   - Fallback: stores in localStorage (opora_bid_tracker_entries) if API fails/unavailable
 *   - Anonymous UUID: opora_anon_uuid in localStorage
 *   - Cross-link: when status === 'lost', shows "Run Lost Bid Autopsy" inline CTA
 *
 * Worker endpoints (specced in WORKER_CHANGES.md — deploy separately):
 *   POST  /api/bid-tracker/add
 *   GET   /api/bid-tracker/list
 *   PATCH /api/bid-tracker/update
 * ──────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────
  const WORKER_BASE       = document.getElementById('bt-app')?.dataset.workerBase || '';
  const LOST_BID_AUTOPSY  = '/pages/lost-bid-autopsy';
  const LS_KEY            = 'opora_bid_tracker_entries';
  const UUID_KEY          = 'opora_anon_uuid';
  const TOAST_MS          = 4000;

  // ── Anonymous UUID ─────────────────────────────────────────
  function getAnonUUID() {
    let id = localStorage.getItem(UUID_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'anon-' + Math.random().toString(36).slice(2);
      localStorage.setItem(UUID_KEY, id);
    }
    return id;
  }

  // ── LocalStorage persistence (fallback) ────────────────────
  function loadLocal() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
    catch { return []; }
  }
  function saveLocal(entries) {
    localStorage.setItem(LS_KEY, JSON.stringify(entries));
  }

  // ── Worker API calls ───────────────────────────────────────
  const UUID = getAnonUUID();

  async function apiAdd(entry) {
    if (!WORKER_BASE) return null;
    const res = await fetch(`${WORKER_BASE}/api/bid-tracker/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Opora-UUID': UUID },
      body: JSON.stringify({ ...entry, user_id: UUID })
    });
    if (!res.ok) return null;
    return await res.json();
  }

  async function apiList() {
    if (!WORKER_BASE) return null;
    const res = await fetch(`${WORKER_BASE}/api/bid-tracker/list`, {
      headers: { 'X-Opora-UUID': UUID }
    });
    if (!res.ok) return null;
    return await res.json();
  }

  async function apiUpdate(id, fields) {
    if (!WORKER_BASE) return null;
    const res = await fetch(`${WORKER_BASE}/api/bid-tracker/update`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Opora-UUID': UUID },
      body: JSON.stringify({ id, ...fields })
    });
    if (!res.ok) return null;
    return await res.json();
  }

  // ── Load entries (worker first, localStorage fallback) ──────
  async function loadEntries() {
    try {
      const data = await apiList();
      if (data && Array.isArray(data.entries)) {
        // Sync to localStorage as backup
        saveLocal(data.entries);
        return data.entries;
      }
    } catch (_) {}
    return loadLocal();
  }

  // ── Stats calculation ──────────────────────────────────────
  function calcStats(entries) {
    const decided   = entries.filter(e => e.status === 'won' || e.status === 'lost');
    const won       = entries.filter(e => e.status === 'won');
    const winRate   = decided.length ? Math.round((won.length / decided.length) * 100) : null;

    const pipeline  = entries
      .filter(e => e.status === 'pending')
      .reduce((s, e) => s + (parseFloat(e.bid_amount) || 0), 0);

    // Won this quarter
    const now = new Date();
    const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const wonQTD = entries
      .filter(e => e.status === 'won' && e.decision_date && new Date(e.decision_date) >= qStart)
      .reduce((s, e) => s + (parseFloat(e.bid_amount) || 0), 0);

    return { winRate, pipeline, wonQTD, total: entries.length };
  }

  function fmt$(n) {
    if (n === null || n === undefined) return '—';
    if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000)     return '$' + Math.round(n / 1000) + 'K';
    return '$' + Math.round(n).toLocaleString();
  }

  // ── Render stats bar ───────────────────────────────────────
  function renderStats(entries) {
    const s = calcStats(entries);
    document.getElementById('stat-win-rate-val').textContent =
      s.winRate !== null ? s.winRate + '%' : '—';
    document.getElementById('stat-pipeline-val').textContent = fmt$(s.pipeline);
    document.getElementById('stat-won-qtd-val').textContent  = fmt$(s.wonQTD);
    document.getElementById('stat-total-val').textContent    = s.total || '—';
  }

  // ── Render table ───────────────────────────────────────────
  let _filterStatus = 'all';

  function renderTable(entries) {
    const tbody   = document.getElementById('bt-tbody');
    const emptyRow = document.getElementById('bt-empty-row');
    const filtered = _filterStatus === 'all'
      ? entries
      : entries.filter(e => e.status === _filterStatus);

    // Remove all non-empty rows
    Array.from(tbody.querySelectorAll('tr:not(#bt-empty-row)')).forEach(r => r.remove());

    if (filtered.length === 0) {
      emptyRow.hidden = false;
      return;
    }
    emptyRow.hidden = true;

    // Sort: most recent first (by created_at or id)
    const sorted = [...filtered].sort((a, b) => {
      const da = new Date(a.created_at || 0), db = new Date(b.created_at || 0);
      return db - da;
    });

    sorted.forEach(entry => {
      const tr = buildRow(entry);
      tbody.appendChild(tr);
      if (entry.status === 'lost') {
        const autopsyRow = buildAutopsyRow(entry);
        tbody.appendChild(autopsyRow);
      }
    });
  }

  function statusBadge(status) {
    return `<span class="bt-badge bt-badge--${status}">${status}</span>`;
  }

  function fmtDate(d) {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }); }
    catch { return d; }
  }

  function buildRow(entry) {
    const tr = document.createElement('tr');
    tr.dataset.entryId = entry.id;
    tr.innerHTML = `
      <td><strong>${escHtml(entry.building_name || '—')}</strong></td>
      <td>${escHtml(entry.city || '—')}${entry.state ? ', ' + escHtml(entry.state) : ''}</td>
      <td>${entry.square_footage ? Number(entry.square_footage).toLocaleString() : '—'}</td>
      <td>${entry.bid_amount ? '$' + Number(entry.bid_amount).toLocaleString(undefined, {minimumFractionDigits:0,maximumFractionDigits:0}) : '—'}</td>
      <td>${fmtDate(entry.submitted_date)}</td>
      <td>${statusBadge(entry.status || 'pending')}</td>
      <td>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          ${entry.status === 'pending' ? `
            <button class="bt-btn bt-btn--sm bt-btn--secondary" data-action="mark-won" data-id="${entry.id}">Won</button>
            <button class="bt-btn bt-btn--sm bt-btn--danger-sm" data-action="mark-lost" data-id="${entry.id}">Lost</button>
          ` : ''}
          <button class="bt-btn bt-btn--sm bt-btn--danger-sm" data-action="delete" data-id="${entry.id}">Delete</button>
        </div>
      </td>
    `;
    return tr;
  }

  function buildAutopsyRow(entry) {
    const qs = new URLSearchParams({
      building: entry.building_name || '',
      city:     entry.city || '',
      state:    entry.state || '',
      amount:   entry.bid_amount || '',
      reason:   entry.loss_reason || '',
      notes:    entry.notes || ''
    });
    const tr = document.createElement('tr');
    tr.classList.add('bt-row-autopsy');
    tr.innerHTML = `
      <td colspan="7">
        <div class="bt-autopsy-cta">
          <strong>→ Lost bid detected.</strong>
          <span>Get clarity on why and how to win next time:</span>
          <a class="bt-btn bt-btn--autopsy" href="${LOST_BID_AUTOPSY}?${qs.toString()}">Run Lost Bid Autopsy →</a>
        </div>
      </td>
    `;
    return tr;
  }

  function escHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  // ── Toast ──────────────────────────────────────────────────
  const toastEl = document.getElementById('bt-toast');
  function showToast(msg, type = 'success') {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.className = `bt-toast bt-toast--${type}`;
    toastEl.hidden = false;
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => { toastEl.hidden = true; }, TOAST_MS);
  }

  // ── Entries state ──────────────────────────────────────────
  let _entries = [];

  function refreshUI() {
    renderStats(_entries);
    renderTable(_entries);
  }

  // ── Form submission ────────────────────────────────────────
  const form   = document.getElementById('bt-add-form');
  const submitBtn = document.getElementById('bt-submit');

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Saving…'; }

    const fd = new FormData(form);
    const entry = {
      id:             crypto.randomUUID ? crypto.randomUUID() : 'local-' + Date.now(),
      building_name:  fd.get('building_name') || '',
      city:           fd.get('city') || '',
      state:          (fd.get('state') || '').toUpperCase(),
      square_footage: fd.get('square_footage') ? Number(fd.get('square_footage')) : null,
      bid_amount:     fd.get('bid_amount') ? Number(fd.get('bid_amount')) : null,
      submitted_date: fd.get('submitted_date') || null,
      status:         fd.get('status') || 'pending',
      loss_reason:    fd.get('loss_reason') || null,
      notes:          fd.get('notes') || '',
      created_at:     new Date().toISOString(),
      user_id:        UUID
    };

    // Validate required fields
    let valid = true;
    document.querySelectorAll('.bt-err').forEach(el => el.textContent = '');
    if (!entry.building_name.trim()) {
      document.getElementById('err-building').textContent = 'Building name is required.';
      valid = false;
    }
    if (!entry.bid_amount || entry.bid_amount <= 0) {
      document.getElementById('err-amount').textContent = 'Enter a valid bid amount.';
      valid = false;
    }
    if (!valid) {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Log bid'; }
      return;
    }

    // Try worker, fall back to localStorage
    let saved = false;
    try {
      const res = await apiAdd(entry);
      if (res && res.ok) { saved = true; entry.id = res.id || entry.id; }
    } catch (_) {}

    if (!saved) {
      _entries.unshift(entry);
      saveLocal(_entries);
    } else {
      _entries.unshift(entry);
      saveLocal(_entries);
    }

    form.reset();
    document.getElementById('bt-loss-reason-wrap').hidden = true;
    refreshUI();
    showToast('Bid logged successfully.', 'success');

    if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Log bid'; }
  });

  // Show/hide loss reason based on status select
  document.getElementById('bt-status')?.addEventListener('change', (e) => {
    const wrap = document.getElementById('bt-loss-reason-wrap');
    if (wrap) wrap.hidden = e.target.value !== 'lost';
  });

  // ── Table action delegation ────────────────────────────────
  document.getElementById('bt-tbody')?.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;

    const action = btn.dataset.action;
    const id     = btn.dataset.id;
    const entry  = _entries.find(en => en.id === id);
    if (!entry) return;

    if (action === 'mark-won' || action === 'mark-lost') {
      const newStatus = action === 'mark-won' ? 'won' : 'lost';
      const fields    = { status: newStatus, decision_date: new Date().toISOString() };
      try { await apiUpdate(id, fields); } catch (_) {}
      Object.assign(entry, fields);
      saveLocal(_entries);
      refreshUI();
      showToast(`Bid marked as ${newStatus}.`, 'success');
    }

    if (action === 'delete') {
      if (!confirm('Delete this bid entry?')) return;
      _entries = _entries.filter(en => en.id !== id);
      saveLocal(_entries);
      refreshUI();
      showToast('Bid deleted.', 'success');
    }
  });

  // ── Filter ─────────────────────────────────────────────────
  document.getElementById('bt-filter-status')?.addEventListener('change', (e) => {
    _filterStatus = e.target.value;
    renderTable(_entries);
  });

  // ── Init ───────────────────────────────────────────────────
  async function init() {
    _entries = await loadEntries();
    refreshUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
